import { useContext, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { Loading, ToastContext } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import { initialContext } from '@/common/context/constants'
import useNotifications from '@/common/hooks/useNotifications/useNotifications'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import useVersionCheck from '@/common/hooks/useVersionCheck/useVersionCheck'
import routes from '@/Router/routes.constants'

import GlobalSearch from '../GlobalSearch/GlobalSearch'
import useGlobalSearchShortcut from '../GlobalSearch/hooks/useGlobalSearchShortcut'
import HelpJuiceChatBot from '../HelpJuiceChatBot/HelpJuiceChatBot'
import SystemAlerts from '../SystemAlerts/SystemAlerts'
import TrialBanner from '../TrialBanner/TrialBanner'
import VersionNotification from '../VersionNotification/VersionNotification'
import Header from './Header/Header'
import useKonamiCode from './hooks/useKonamiCode/useKonamiCode'
import useReturnPortals from './hooks/useReturnPortals/useReturnPortals'
import SideBarMenu from './SideBarMenu/SideBarMenu'
import WelcomeScreen from './WelcomeScreen/WelcomeScreen'
import { getOptions } from './constants'
import * as Styles from './styles'

const PageLayout = () => {
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const navigate = useNavigate()
  const {
    notifications,
    loadingNotifications,
    handleMarkAllAsRead,
    loadingMarkAllAsRead,
    notificationsPage,
    setNotificationsPage,
    notificationsCountPage,
    showNotificationsMenu,
    setShowNotificationsMenu,
    onClickNotification,
  } = useNotifications({
    setToastInfo,
    updateContext,
    storeId: info?.selectedStore?.id,
    navigate,
  })
  const { hasPermission } = useUserPermissions()
  const { onChangeReturnPortal, getSelectedReturnPortal } = useReturnPortals({
    returnPortals: info?.returnPortals,
    selectedReturnPortal: info?.selectedReturnPortal,
    updateContext,
  })
  const { hasNewVersion } = useVersionCheck()
  const konamiActive = useKonamiCode()
  const { show: showGlobalSearch, onOpen: onOpenGlobalSearch, onClose: onCloseGlobalSearch } = useGlobalSearchShortcut()

  useEffect(() => {
    if (!localStorage.jwt) {
      navigate(routes.loginPage)
    }
  }, [navigate])

  const onLogout = () => {
    localStorage.clear()
    updateContext(initialContext)

    navigate(routes.loginPage, { replace: true })
  }

  const onEditProfile = () => {
    navigate(routes.accountPage.profile)
  }

  if (info?.requires_activation) return <WelcomeScreen />

  if (!info?.selectedReturnPortal?.id) return <Loading data-testid="loading" show />

  return (
    <>
      <Styles.MainContainer>
        <Styles.HeaderContainer>
          <Header
            merchantName={info?.name}
            homeLink={routes.dashboardPage}
            onLogout={onLogout}
            showNotifications
            notificationsCounter={info?.selectedStore?.num_unread_notifications}
            notifications={notifications?.results}
            loadingNotifications={loadingNotifications || loadingMarkAllAsRead}
            notificationsPreviousPage={notifications?.previous}
            notificationsNextPage={notifications?.next}
            notificationsCountPage={notificationsCountPage}
            notificationsPage={notificationsPage}
            notificationsOnChangePage={setNotificationsPage}
            onClickMarkAsRead={handleMarkAllAsRead}
            onClickNotification={onClickNotification}
            showNotificationsMenu={showNotificationsMenu}
            onClickShowNotificationsMenu={setShowNotificationsMenu}
            userInfo={info?.userInfo}
            onEditProfile={onEditProfile}
            onOpenSearch={onOpenGlobalSearch}
          />
        </Styles.HeaderContainer>
        <Styles.AlertContainer>
          <VersionNotification show={hasNewVersion} />
          <TrialBanner status={info?.status} trialPeriodDate={info?.trial_period_date} />
          <SystemAlerts alerts={info?.systemAlerts} />
        </Styles.AlertContainer>
        <Styles.SideBarContainer>
          <SideBarMenu
            options={getOptions({
              analyticsUrl: info?.selectedStore?.analytics_url,
              subscriptionPlan: info?.subscription_plan,
              hasPermission,
              allowInventory: info?.selectedStore?.preferences?.allows_compliance_hub,
              allowPerformanceInsights: info?.selectedStore?.preferences?.allows_performance_insights,
              allowsUnifiedDashboard: info?.allows_unified_dashboard,
            })}
            currentPath={window.location.pathname}
            selectedReturnPortal={getSelectedReturnPortal()}
            returnPortals={info?.returnPortals}
            onChangeReturnPortal={onChangeReturnPortal}
            supportDashboardUrl={info?.support_dashboard_url}
          />
        </Styles.SideBarContainer>
        <Styles.ContentContainer showPattern={konamiActive}>
          <Outlet />
        </Styles.ContentContainer>
      </Styles.MainContainer>
      <GlobalSearch show={showGlobalSearch} onClose={onCloseGlobalSearch} />
      <HelpJuiceChatBot />
    </>
  )
}

export default PageLayout
